import type { ReactNode, CSSProperties } from "react";
import { useRef, useState, useCallback, useEffect } from "react";
import { PopupBase } from "@/components/generic/popup/PopupBase.js";
import { fadeInOut } from "@/utils/fadeInOut.js";
import { getTimeMs } from "@rosette/ts/src/util/utils.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import type { MotionProps } from "framer-motion";


export type PopupAlignment = "center" | "left" | "right";


// Tracks every currently-open popup so that opening one closes the rest.
// Without this, clicking a second popup's trigger calls preventDefault(),
// which makes the first popup's outside-click listener bail out.
const openPopups = new Set<(resetImmediately?: boolean) => void>();


export interface PopupFunctions {
    isOpen: () => boolean;
    open: (anchorButton?: HTMLElement, alignment?: PopupAlignment) => void;
    close: (resetImmediately?: boolean) => void;
}


interface PopupProps {
    children?: ReactNode;
    setPopupFunctions: (popup: PopupFunctions) => void;
    minPopupWidthPx?: number;

    containerClassName?: string;
    contentsClassName?: string;
    motionParams?: MotionProps;

    onOpen?: () => void;
    onClose?: () => void;
}


export function Popup({
    children,
    setPopupFunctions,
    minPopupWidthPx,
    containerClassName,
    contentsClassName,
    motionParams,
    onOpen,
    onClose,
}: PopupProps) {
    if (!motionParams) {
        motionParams = fadeInOut();
    }

    const [containerStyles, setContainerStyles] = useState<CSSProperties | undefined>(undefined);
    const contentsRef = useRef<HTMLDivElement | null>(null);
    const [visible, setVisible] = useState(false);
    const lastCloseTimeRef = useRef<number>(getTimeMs());
    const closeResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // A stable handle to this popup's latest close function, used to
    // register/identify it in the global openPopups registry.
    const closeFnRef = useRef<(resetImmediately?: boolean) => void>(() => {});
    const stableCloseRef = useRef((resetImmediately?: boolean) => {
        closeFnRef.current(resetImmediately);
    });

    const close = useCallback((resetImmediately?: boolean) => {
        if (visible) {
            setVisible(false);
            lastCloseTimeRef.current = getTimeMs();
            openPopups.delete(stableCloseRef.current);
            if (onClose) {
                onClose();
            }
        }

        const reset = () => setContainerStyles({
            opacity: 0, left: 0, top: 0,
        });

        if (resetImmediately) {
            reset();
        } else {
            if (closeResetTimerRef.current) {
                clearTimeout(closeResetTimerRef.current);
                closeResetTimerRef.current = null;
            }
            closeResetTimerRef.current = setTimeout(reset, 500);
        }
    }, [visible, setVisible, lastCloseTimeRef, onClose, setContainerStyles]);

    closeFnRef.current = close;

    useEffect(() => {
        const stableClose = stableCloseRef.current;
        return () => {
            openPopups.delete(stableClose);
        };
    }, []);

    const open = useCallback((anchorButton?: HTMLElement, alignment?: PopupAlignment) => {
        if (visible || getTimeMs() - lastCloseTimeRef.current < 500)
            return;

        // Close any other popups that are currently open.
        for (const otherClose of [...openPopups]) {
            if (otherClose !== stableCloseRef.current) {
                otherClose(true);
            }
        }
        openPopups.add(stableCloseRef.current);

        if (closeResetTimerRef.current) {
            clearTimeout(closeResetTimerRef.current);
            closeResetTimerRef.current = null;
        }

        if (anchorButton) {
            const bounds = anchorButton.getBoundingClientRect();
            const minPopupWidth = (minPopupWidthPx ?? 200) + 20;

            let targetLeft;
            if (alignment === "center") {
                targetLeft = (bounds.left + bounds.right - minPopupWidth) / 2;
            } else if (alignment === "left") {
                targetLeft = bounds.right - minPopupWidth + 10;
            } else if (alignment === "right") {
                targetLeft = bounds.left - 10;
            } else {
                throw new Error(`Unknown alignment ${alignment}`);
            }

            const maxLeft = window.innerWidth - minPopupWidth;
            setContainerStyles({
                opacity: "1",
                left: Math.max(0, Math.min(targetLeft, maxLeft)) + "px",
                top: (bounds.bottom + window.scrollY) + "px",
            });
        }

        setVisible(true);
        if (onOpen) {
            onOpen();
        }
    }, [visible, minPopupWidthPx, setVisible, onOpen, setContainerStyles]);

    setPopupFunctions({
        isOpen: () => visible,
        open, close,
    });

    return (
        <PopupBase
            visible={visible}
            close={close}
            contentsRef={contentsRef}
            motionParams={motionParams}>

            <div
                className={cn(
                    "absolute",
                    containerClassName ?? null,
                )}
                style={containerStyles}>

                <div
                    className={contentsClassName}
                    ref={contentsRef}>

                    {children}
                </div>
            </div>
        </PopupBase>
    );
}
